import {observable} from "mobx";
import Config from './../constants/Config';
import {
    authSession,
    authenticatedFetch,
    StaleAuthSessionError,
    unauthenticatedFetch,
} from './AuthSession';

class Network {

    isLoading = observable.box(false);
    modal = observable.box(false);
    userProfile = observable({});
    activeDoctors = observable([]);
    newDoctors = observable([]);
    authState = observable.box('booting');
    authError = observable.box(null);
    history = null;

}

const network = new Network();
export default network;

function clearNetworkSession() {
    const shouldRedirect = network.authState.get() !== 'anonymous';
    network.userProfile = null;
    network.authError.set(null);
    network.authState.set('anonymous');

    if (network.history && shouldRedirect) {
        network.history.push('/login');
    }
}

authSession.setInvalidatedHandler(clearNetworkSession);

function markSessionUnavailable(error) {
    const value = error && error.message ? error.message : error;
    const message = typeof value === 'string' && value.length > 0
        ? value
        : 'Не удалось загрузить данные. Попробуйте ещё раз.';
    network.userProfile = null;
    network.authError.set(message);
    network.authState.set('unavailable');
}

function handleAuthenticationFlowError(error) {
    if (error instanceof StaleAuthSessionError) {
        return;
    }

    if (authSession.getRefreshToken()) {
        markSessionUnavailable(error);
        return;
    }

    clearNetworkSession();
}

export function handleUnauthorized() {
    authSession.clear();
}

export async function login(loginValue, password) {
    network.userProfile = null;
    network.authError.set(null);
    network.authState.set('authenticating');

    try {
        await authSession.login(loginValue, password);
        await getProfile();
    } catch (error) {
        handleAuthenticationFlowError(error);
        throw error;
    }
}

export function logout() {
    network.userProfile = null;
    network.authError.set(null);
    network.authState.set('logging-out');
    return authSession.logout();
}

export async function restoreSession() {
    network.userProfile = null;
    network.authError.set(null);
    network.authState.set('booting');

    try {
        await authSession.restore();
        await getProfile();
    } catch (error) {
        handleAuthenticationFlowError(error);
        throw error;
    }
}

export async function retrySession() {
    network.authError.set(null);
    network.authState.set('booting');

    try {
        if (!authSession.isAuthenticated()) {
            await authSession.restore();
        }
        await getProfile();
    } catch (error) {
        handleAuthenticationFlowError(error);
        throw error;
    }
}

export function getProfile() {
    return new Promise(function (resolve, reject) {
        authenticatedFetch(Config.apiDomain + "/v1/user/get-profile", {
            method: "GET",
            mode: 'cors',
            'Access-Control-Allow-Origin': 'http://localhost:80',
            'Access-Control-Max-Age': 3600,
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json'
            },
        })
            .then(result => {
                return result.json();
            })
            .then(res => {
                console.log('getProfile:::: ' + res);
                if (res.status === 200) {
                    network.userProfile = res.profile;
                    network.authError.set(null);
                    network.authState.set('authenticated');
                    resolve();
                } else {
                    if (res.status === 401) {
                        handleUnauthorized();
                        reject(res.message || 'Сессия истекла');
                    } else {
                        reject(res.message);
                    }
                }
            })
            .catch(err => {
                console.log('error: ' + err);
                reject(err);
            });
    });
}

export function getUnits() {
    return new Promise(function (resolve, reject) {
        authenticatedFetch(Config.apiDomain + "/v1/units/get-units", {
            method: "GET",
            mode: 'cors',
            'Access-Control-Allow-Origin': 'http://localhost:80',
            'Access-Control-Max-Age': 3600,
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json',
            },
        })
            .then(result => {
                return result.json();
            })
            .then(res => {
                console.log(res);
                if (res.status === 200) {
                    resolve(res.units);
                } else {
                    if (res.status === 401) {
                        handleUnauthorized();
                    } else {
                        reject(res.message);
                    }
                }
            })
            .catch(err => {
                console.log('error: ' + err);
                reject(err);
            });
    });
}

export function saveUnit(id, name) {
    return new Promise(function (resolve, reject) {
        authenticatedFetch(Config.apiDomain + "/v1/units/save", {
            method: "POST",
            mode: 'cors',
            'Access-Control-Allow-Origin': 'http://localhost:80',
            'Access-Control-Max-Age': 3600,
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                id,
                name,
            })
        })
            .then(result => {
                return result.json();
            })
            .then(res => {
                console.log(res);
                if (res.status === 200) {
                    resolve();
                } else {
                    if (res.status === 401) {
                        handleUnauthorized();
                    } else {
                        reject(res.message || 'Ошибка базы данных!');
                    }
                }
            })
            .catch(err => {
                console.log('error: ' + err);
                reject(err);
            });
    });
}

export function deleteUnit(id) {
    return new Promise(function (resolve, reject) {
        authenticatedFetch(Config.apiDomain + "/v1/units/delete-unit?id=" + id, {
            method: "GET",
            mode: 'cors',
            'Access-Control-Allow-Origin': 'http://localhost:80',
            'Access-Control-Max-Age': 3600,
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json',
            },
        })
            .then(result => {
                return result.json();
            })
            .then(res => {
                console.log(res);
                if (res.status === 200) {
                    resolve();
                } else {
                    if (res.status === 401) {
                        handleUnauthorized();
                    } else {
                        reject(res.message || 'Ошибка базы данных!');
                    }
                }
            })
            .catch(err => {
                console.log('error: ' + err);
                reject(err);
            });
    });
}

export function getRanks() {
    return new Promise(function (resolve, reject) {
        authenticatedFetch(Config.apiDomain + "/v1/rank/get-ranks", {
            method: "GET",
            mode: 'cors',
            'Access-Control-Allow-Origin': 'http://localhost:80',
            'Access-Control-Max-Age': 3600,
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json',
            },
        })
            .then(result => {
                return result.json();
            })
            .then(res => {
                console.log(res);
                if (res.status === 200) {
                    resolve(res.ranks);
                } else {
                    if (res.status === 401) {
                        handleUnauthorized();
                    } else {
                        reject(res.message);
                    }
                }
            })
            .catch(err => {
                console.log('error: ' + err);
                reject(err);
            });
    });
}

export function saveRank(id, name) {
    return new Promise(function (resolve, reject) {
        authenticatedFetch(Config.apiDomain + "/v1/rank/save", {
            method: "POST",
            mode: 'cors',
            'Access-Control-Allow-Origin': 'http://localhost:80',
            'Access-Control-Max-Age': 3600,
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                id,
                name,
            })
        })
            .then(result => {
                return result.json();
            })
            .then(res => {
                console.log(res);
                if (res.status === 200) {
                    resolve();
                } else {
                    if (res.status === 401) {
                        handleUnauthorized();
                    } else {
                        reject(res.message || 'Ошибка базы данных!');
                    }
                }
            })
            .catch(err => {
                console.log('error: ' + err);
                reject(err);
            });
    });
}

export function deleteRank(id) {
    return new Promise(function (resolve, reject) {
        authenticatedFetch(Config.apiDomain + "/v1/rank/delete-rank?id=" + id, {
            method: "GET",
            mode: 'cors',
            'Access-Control-Allow-Origin': 'http://localhost:80',
            'Access-Control-Max-Age': 3600,
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json',
            },
        })
            .then(result => {
                return result.json();
            })
            .then(res => {
                console.log(res);
                if (res.status === 200) {
                    resolve();
                } else {
                    if (res.status === 401) {
                        handleUnauthorized();
                    } else {
                        reject(res.message || 'Ошибка базы данных!');
                    }
                }
            })
            .catch(err => {
                console.log('error: ' + err);
                reject(err);
            });
    });
}

/** Справочник жалоб (dic_targets) — GET /v1/target/get-targets-short */
export function getTargetsShort() {
    return new Promise(function (resolve, reject) {
        authenticatedFetch(Config.apiDomain + "/v1/target/get-targets-short", {
            method: "GET",
            mode: 'cors',
            'Access-Control-Allow-Origin': 'http://localhost:80',
            'Access-Control-Max-Age': 3600,
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json',
            },
        })
            .then(result => {
                return result.json();
            })
            .then(res => {
                if (res.status === 200) {
                    resolve(res.targets || []);
                } else {
                    if (res.status === 401) {
                        handleUnauthorized();
                    } else {
                        reject(res.message);
                    }
                }
            })
            .catch(err => {
                reject(err);
            });
    });
}

/** Полный список жалоб справочника для админки (все поля). */
export function getTargetsList() {
    return new Promise(function (resolve, reject) {
        authenticatedFetch(Config.apiDomain + "/v1/target/get-targets-list", {
            method: "GET",
            mode: 'cors',
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json',
            },
        })
            .then(result => result.json())
            .then(res => {
                if (res.status === 200) resolve(res.targets || []);
                else if (res.status === 401) handleUnauthorized();
                else reject(res.message);
            })
            .catch(reject);
    });
}

/** Сохранить жалобу в справочнике (создание/редактирование). */
export function saveTarget(id, name, description, ageCategory, sex = null, complaintCategory = '') {
    return new Promise(function (resolve, reject) {
        const body = {
            id: id || 0,
            name: name || '',
            description: description || '',
            complaint_category: (complaintCategory && String(complaintCategory).trim()) || '',
            age_category: ['child', 'adult', 'elderly'].includes(ageCategory) ? ageCategory : 'adult',
            // API ожидает boolean|null: true=мужской, false=женский, null=для всех
            sex: sex === null ? null : (sex === true || sex === false ? sex : null),
        };
        authenticatedFetch(Config.apiDomain + "/v1/target/save-target", {
            method: "POST",
            mode: 'cors',
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(body)
        })
            .then(result => result.json())
            .then(res => {
                if (res.status === 200) resolve();
                else if (res.status === 401) handleUnauthorized();
                else reject(res.message);
            })
            .catch(reject);
    });
}

/** Список целей (для сегментации каналов) */
export function getGoalsList() {
    return new Promise(function (resolve, reject) {
        authenticatedFetch(Config.apiDomain + "/v1/goal/get-goals", {
            method: "GET",
            mode: 'cors',
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json',
            },
        })
            .then(result => result.json())
            .then(res => {
                if (res.success && res.goals) resolve(res.goals);
                else if (res.status === 401) handleUnauthorized();
                else reject(res.error || res.message);
            })
            .catch(reject);
    });
}

/** Полный справочник целей для админки */
export function getGoalsDictionaryList() {
    return new Promise(function (resolve, reject) {
        authenticatedFetch(Config.apiDomain + "/v1/goal/get-goals-list", {
            method: "GET",
            mode: 'cors',
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json',
            },
        })
            .then(result => result.json())
            .then(res => {
                if (res.status === 200) resolve(res.goals || []);
                else if (res.status === 401) handleUnauthorized();
                else reject(res.message || res.error);
            })
            .catch(reject);
    });
}

/** Сохранить цель в справочнике (админка) */
export function saveGoalDictionary(payload) {
    return new Promise(function (resolve, reject) {
        authenticatedFetch(Config.apiDomain + "/v1/goal/save-goal-dictionary", {
            method: "POST",
            mode: 'cors',
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(payload)
        })
            .then(result => result.json())
            .then(res => {
                if (res.status === 200) resolve();
                else if (res.status === 401) handleUnauthorized();
                else reject(res.message || res.error);
            })
            .catch(reject);
    });
}

/** Удалить жалобу из справочника. */
export function deleteTargetFromDictionary(id) {
    return new Promise(function (resolve, reject) {
        authenticatedFetch(Config.apiDomain + "/v1/target/delete-target-from-dictionary?id=" + id, {
            method: "GET",
            mode: 'cors',
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json',
            },
        })
            .then(result => result.json())
            .then(res => {
                if (res.status === 200) resolve();
                else if (res.status === 401) handleUnauthorized();
                else reject(res.message);
            })
            .catch(reject);
    });
}

export function getDigrees() {
    return new Promise(function (resolve, reject) {
        authenticatedFetch(Config.apiDomain + "/v1/digree/get-digrees", {
            method: "GET",
            mode: 'cors',
            'Access-Control-Allow-Origin': 'http://localhost:80',
            'Access-Control-Max-Age': 3600,
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json',
            },
        })
            .then(result => {
                return result.json();
            })
            .then(res => {
                console.log(res);
                if (res.status === 200) {
                    resolve(res.digrees);
                } else {
                    if (res.status === 401) {
                        handleUnauthorized();
                    } else {
                        reject(res.message);
                    }
                }
            })
            .catch(err => {
                console.log('error: ' + err);
                reject(err);
            });
    });
}

export function saveDigree(id, name) {
    return new Promise(function (resolve, reject) {
        authenticatedFetch(Config.apiDomain + "/v1/digree/save", {
            method: "POST",
            mode: 'cors',
            'Access-Control-Allow-Origin': 'http://localhost:80',
            'Access-Control-Max-Age': 3600,
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                id,
                name,
            })
        })
            .then(result => {
                return result.json();
            })
            .then(res => {
                console.log(res);
                if (res.status === 200) {
                    resolve();
                } else {
                    if (res.status === 401) {
                        handleUnauthorized();
                    } else {
                        reject(res.message || 'Ошибка базы данных!');
                    }
                }
            })
            .catch(err => {
                console.log('error: ' + err);
                reject(err);
            });
    });
}

export function deleteDigree(id) {
    return new Promise(function (resolve, reject) {
        authenticatedFetch(Config.apiDomain + "/v1/digree/delete-digree?id=" + id, {
            method: "GET",
            mode: 'cors',
            'Access-Control-Allow-Origin': 'http://localhost:80',
            'Access-Control-Max-Age': 3600,
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json',
            },
        })
            .then(result => {
                return result.json();
            })
            .then(res => {
                console.log(res);
                if (res.status === 200) {
                    resolve();
                } else {
                    if (res.status === 401) {
                        handleUnauthorized();
                    } else {
                        reject(res.message || 'Ошибка базы данных!');
                    }
                }
            })
            .catch(err => {
                console.log('error: ' + err);
                reject(err);
            });
    });
}

export function setAnketa(id, value) {
    return new Promise(function (resolve, reject) {
        authenticatedFetch(Config.apiDomain + "/v1/anketa/set-anketa", {
            method: "POST",
            mode: 'cors',
            'Access-Control-Allow-Origin': 'http://localhost:80',
            'Access-Control-Max-Age': 3600,
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                id,
                value
            })
        })
            .then(result => {
                return result.json();
            })
            .then(res => {
                console.log(res);
                if (res.status === 200) {
                    resolve();
                } else {
                    if (res.status === 401) {
                        handleUnauthorized();
                    } else {
                        reject(res.message || 'Ошибка базы данных!');
                    }
                }
            })
            .catch(err => {
                console.log('error: ' + err);
                reject(err);
            });
    });
}

export function getAnketas() {
    return new Promise(function (resolve, reject) {
        authenticatedFetch(Config.apiDomain + "/v1/anketa/get-anketas", {
            method: "GET",
            mode: 'cors',
            'Access-Control-Allow-Origin': 'http://localhost:80',
            'Access-Control-Max-Age': 3600,
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json',
            },
        })
            .then(result => {
                return result.json();
            })
            .then(res => {
                console.log(res);
                if (res.status === 200) {
                    resolve(res.anketas);
                } else {
                    if (res.status === 401) {
                        handleUnauthorized();
                    } else {
                        reject(res.message);
                    }
                }
            })
            .catch(err => {
                console.log('error: ' + err);
                reject(err);
            });
    });
}

export function saveAnketa(id, name) {
    return new Promise(function (resolve, reject) {
        authenticatedFetch(Config.apiDomain + "/v1/anketa/save-anketa", {
            method: "POST",
            mode: 'cors',
            'Access-Control-Allow-Origin': 'http://localhost:80',
            'Access-Control-Max-Age': 3600,
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                id,
                name,
            })
        })
            .then(result => {
                return result.json();
            })
            .then(res => {
                console.log(res);
                if (res.status === 200) {
                    resolve();
                } else {
                    if (res.status === 401) {
                        handleUnauthorized();
                    } else {
                        reject(res.message || 'Ошибка базы данных!');
                    }
                }
            })
            .catch(err => {
                console.log('error: ' + err);
                reject(err);
            });
    });
}

export function deleteAnketa(id) {
    return new Promise(function (resolve, reject) {
        authenticatedFetch(Config.apiDomain + "/v1/anketa/delete-anketa?id=" + id, {
            method: "GET",
            mode: 'cors',
            'Access-Control-Allow-Origin': 'http://localhost:80',
            'Access-Control-Max-Age': 3600,
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json',
            },
        })
            .then(result => {
                return result.json();
            })
            .then(res => {
                console.log(res);
                if (res.status === 200) {
                    resolve();
                } else {
                    if (res.status === 401) {
                        handleUnauthorized();
                    } else {
                        reject(res.message || 'Ошибка базы данных!');
                    }
                }
            })
            .catch(err => {
                console.log('error: ' + err);
                reject(err);
            });
    });
}

export function getParts(age_category = null) {
    return new Promise(function (resolve, reject) {
        let path = "/v1/anketa/get-parts";
        if (age_category) {
            path += '?age_category=' + encodeURIComponent(age_category);
        }
        authenticatedFetch(Config.apiDomain + path, {
            method: "GET",
            mode: 'cors',
            'Access-Control-Allow-Origin': 'http://localhost:80',
            'Access-Control-Max-Age': 3600,
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json',
            },
        })
            .then(result => {
                return result.json();
            })
            .then(res => {
                console.log(res);
                if (res.status === 200) {
                    resolve(res.parts);
                } else {
                    if (res.status === 401) {
                        handleUnauthorized();
                    } else {
                        reject(res.message);
                    }
                }
            })
            .catch(err => {
                console.log('error: ' + err);
                reject(err);
            });
    });
}

export function savePart(id, name, anketa_id, age_category = null) {
    return new Promise(function (resolve, reject) {
        authenticatedFetch(Config.apiDomain + "/v1/anketa/save", {
            method: "POST",
            mode: 'cors',
            'Access-Control-Allow-Origin': 'http://localhost:80',
            'Access-Control-Max-Age': 3600,
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                id,
                name,
                anketa_id,
                age_category,
            })
        })
            .then(result => {
                return result.json();
            })
            .then(res => {
                console.log(res);
                if (res.status === 200) {
                    resolve();
                } else {
                    if (res.status === 401) {
                        handleUnauthorized();
                    } else {
                        reject(res.message || 'Ошибка базы данных!');
                    }
                }
            })
            .catch(err => {
                console.log('error: ' + err);
                reject(err);
            });
    });
}

export function deletePart(id) {
    return new Promise(function (resolve, reject) {
        authenticatedFetch(Config.apiDomain + "/v1/anketa/delete-part?id=" + id, {
            method: "GET",
            mode: 'cors',
            'Access-Control-Allow-Origin': 'http://localhost:80',
            'Access-Control-Max-Age': 3600,
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json',
            },
        })
            .then(result => {
                return result.json();
            })
            .then(res => {
                console.log(res);
                if (res.status === 200) {
                    resolve();
                } else {
                    if (res.status === 401) {
                        handleUnauthorized();
                    } else {
                        reject(res.message || 'Ошибка базы данных!');
                    }
                }
            })
            .catch(err => {
                console.log('error: ' + err);
                reject(err);
            });
    });
}

export function getSections(age_category = null) {
    return new Promise(function (resolve, reject) {
        let path = "/v1/anketa/get-sections";
        if (age_category) {
            path += '?age_category=' + encodeURIComponent(age_category);
        }
        authenticatedFetch(Config.apiDomain + path, {
            method: "GET",
            mode: 'cors',
            'Access-Control-Allow-Origin': 'http://localhost:80',
            'Access-Control-Max-Age': 3600,
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json',
            },
        })
            .then(result => {
                return result.json();
            })
            .then(res => {
                console.log(res);
                if (res.status === 200) {
                    resolve(res.sections);
                } else {
                    if (res.status === 401) {
                        handleUnauthorized();
                    } else {
                        reject(res.message);
                    }
                }
            })
            .catch(err => {
                console.log('error: ' + err);
                reject(err);
            });
    });
}

export function saveSection(id, name, part, age_category = null) {
    return new Promise(function (resolve, reject) {
        authenticatedFetch(Config.apiDomain + "/v1/anketa/save-section", {
            method: "POST",
            mode: 'cors',
            'Access-Control-Allow-Origin': 'http://localhost:80',
            'Access-Control-Max-Age': 3600,
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                id,
                name,
                part,
                age_category,
            })
        })
            .then(result => {
                return result.json();
            })
            .then(res => {
                console.log(res);
                if (res.status === 200) {
                    resolve();
                } else {
                    if (res.status === 401) {
                        handleUnauthorized();
                    } else {
                        reject(res.message || 'Ошибка базы данных!');
                    }
                }
            })
            .catch(err => {
                console.log('error: ' + err);
                reject(err);
            });
    });
}

export function deleteSection(id) {
    return new Promise(function (resolve, reject) {
        authenticatedFetch(Config.apiDomain + "/v1/anketa/delete-section?id=" + id, {
            method: "GET",
            mode: 'cors',
            'Access-Control-Allow-Origin': 'http://localhost:80',
            'Access-Control-Max-Age': 3600,
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json',
            },
        })
            .then(result => {
                return result.json();
            })
            .then(res => {
                console.log(res);
                if (res.status === 200) {
                    resolve();
                } else {
                    if (res.status === 401) {
                        handleUnauthorized();
                    } else {
                        reject(res.message || 'Ошибка базы данных!');
                    }
                }
            })
            .catch(err => {
                console.log('error: ' + err);
                reject(err);
            });
    });
}

export function getQuestions(age_category = null) {
    return new Promise(function (resolve, reject) {
        let path = "/v1/anketa/get-questions";
        if (age_category) {
            path += '?age_category=' + encodeURIComponent(age_category);
        }
        authenticatedFetch(Config.apiDomain + path, {
            method: "GET",
            mode: 'cors',
            'Access-Control-Allow-Origin': 'http://localhost:80',
            'Access-Control-Max-Age': 3600,
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json',
            },
        })
            .then(result => {
                return result.json();
            })
            .then(res => {
                console.log(res);
                if (res.status === 200) {
                    resolve(res.questions);
                } else {
                    if (res.status === 401) {
                        handleUnauthorized();
                    } else {
                        reject(res.message);
                    }
                }
            })
            .catch(err => {
                console.log('error: ' + err);
                reject(err);
            });
    });
}

export function saveQuestion(id, name, part, section, sex, answers, parent_id, parent_answers_id, age_category = null) {
    return new Promise(function (resolve, reject) {
        authenticatedFetch(Config.apiDomain + "/v1/anketa/save-question", {
            method: "POST",
            mode: 'cors',
            'Access-Control-Allow-Origin': 'http://localhost:80',
            'Access-Control-Max-Age': 3600,
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                id,
                name,
                part,
                section,
                sex,
                answers,
                parent_id,
                parent_answers_id,
                age_category,
            })
        })
            .then(result => {
                return result.json();
            })
            .then(res => {
                console.log(res);
                if (res.status === 200) {
                    resolve();
                } else {
                    if (res.status === 401) {
                        handleUnauthorized();
                    } else {
                        reject(res.message || 'Ошибка базы данных!');
                    }
                }
            })
            .catch(err => {
                console.log('error: ' + err);
                reject(err);
            });
    });
}

export function deleteQuestion(id) {
    return new Promise(function (resolve, reject) {
        authenticatedFetch(Config.apiDomain + "/v1/anketa/delete-question?id=" + id, {
            method: "GET",
            mode: 'cors',
            'Access-Control-Allow-Origin': 'http://localhost:80',
            'Access-Control-Max-Age': 3600,
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json',
            },
        })
            .then(result => {
                return result.json();
            })
            .then(res => {
                console.log(res);
                if (res.status === 200) {
                    resolve();
                } else {
                    if (res.status === 401) {
                        handleUnauthorized();
                    } else {
                        reject(res.message || 'Ошибка базы данных!');
                    }
                }
            })
            .catch(err => {
                console.log('error: ' + err);
                reject(err);
            });
    });
}

export function getAvatarComplaintLinkDictionaries() {
    return new Promise(function (resolve, reject) {
        authenticatedFetch(Config.apiDomain + "/v1/anketa/get-avatar-complaint-link-dictionaries", {
            method: "GET",
            mode: 'cors',
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json',
            },
        })
            .then(result => result.json())
            .then(res => {
                if (res.status === 200) {
                    resolve({
                        targets: res.targets || [],
                        anketas: res.anketas || [],
                        parts: res.parts || [],
                    });
                } else if (res.status === 401) {
                    handleUnauthorized();
                } else {
                    reject(res.message || 'Ошибка загрузки словарей');
                }
            })
            .catch(reject);
    });
}

export function getTargetPartLinks(filters = {}) {
    const params = new URLSearchParams();
    Object.keys(filters).forEach((k) => {
        if (filters[k] !== undefined && filters[k] !== null && filters[k] !== '') {
            params.append(k, filters[k]);
        }
    });
    return new Promise(function (resolve, reject) {
        authenticatedFetch(Config.apiDomain + "/v1/anketa/get-target-part-links?" + params.toString(), {
            method: "GET",
            mode: 'cors',
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json',
            },
        })
            .then(result => result.json())
            .then(res => {
                if (res.status === 200) resolve(res.rows || []);
                else if (res.status === 401) handleUnauthorized();
                else reject(res.message || 'Ошибка загрузки связей');
            })
            .catch(reject);
    });
}

export function saveTargetPartLink(payload) {
    return new Promise(function (resolve, reject) {
        authenticatedFetch(Config.apiDomain + "/v1/anketa/save-target-part-link", {
            method: "POST",
            mode: 'cors',
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(payload || {})
        })
            .then(result => result.json())
            .then(res => {
                if (res.status === 200) resolve(res.row || null);
                else if (res.status === 401) handleUnauthorized();
                else reject(res.message || 'Ошибка сохранения связи');
            })
            .catch(reject);
    });
}

export function deleteTargetPartLink(id) {
    return new Promise(function (resolve, reject) {
        authenticatedFetch(Config.apiDomain + "/v1/anketa/delete-target-part-link?id=" + id, {
            method: "GET",
            mode: 'cors',
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json',
            },
        })
            .then(result => result.json())
            .then(res => {
                if (res.status === 200) resolve();
                else if (res.status === 401) handleUnauthorized();
                else reject(res.message || 'Ошибка удаления связи');
            })
            .catch(reject);
    });
}

export function getAnalisys() {
    return new Promise(function (resolve, reject) {
        authenticatedFetch(Config.apiDomain + "/v1/analis/get-analisys", {
            method: "GET",
            mode: 'cors',
            'Access-Control-Allow-Origin': 'http://localhost:80',
            'Access-Control-Max-Age': 3600,
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json',
            },
        })
            .then(result => {
                return result.json();
            })
            .then(res => {
                console.log(res);
                if (res.status === 200) {
                    resolve(res.analisys);
                } else {
                    if (res.status === 401) {
                        handleUnauthorized();
                    } else {
                        reject(res.message);
                    }
                }
            })
            .catch(err => {
                console.log('error: ' + err);
                reject(err);
            });
    });
}

export function saveAnalis(id, name, description, unit, sex, params, min, max, group_id, digit_round) {
    return new Promise(function (resolve, reject) {
        authenticatedFetch(Config.apiDomain + "/v1/analis/save", {
            method: "POST",
            mode: 'cors',
            'Access-Control-Allow-Origin': 'http://localhost:80',
            'Access-Control-Max-Age': 3600,
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                id,
                name,
                description,
                unit,
                sex,
                params,
                min,
                max,
                group_id,
                digit_round,
            })
        })
            .then(result => {
                return result.json();
            })
            .then(res => {
                console.log(res);
                if (res.status === 200) {
                    resolve();
                } else {
                    if (res.status === 401) {
                        handleUnauthorized();
                    } else {
                        reject(res.message || 'Ошибка базы данных!');
                    }
                }
            })
            .catch(err => {
                console.log('error: ' + err);
                reject(err);
            });
    });
}

export function deleteAnalis(id) {
    return new Promise(function (resolve, reject) {
        authenticatedFetch(Config.apiDomain + "/v1/analis/delete-analis?id=" + id, {
            method: "GET",
            mode: 'cors',
            'Access-Control-Allow-Origin': 'http://localhost:80',
            'Access-Control-Max-Age': 3600,
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json',
            },
        })
            .then(result => {
                return result.json();
            })
            .then(res => {
                console.log(res);
                if (res.status === 200) {
                    resolve();
                } else {
                    if (res.status === 401) {
                        handleUnauthorized();
                    } else {
                        reject(res.message || 'Ошибка базы данных!');
                    }
                }
            })
            .catch(err => {
                console.log('error: ' + err);
                reject(err);
            });
    });
}

export function getSpecs() {
    return new Promise(function (resolve, reject) {
        authenticatedFetch(Config.apiDomain + "/v1/specialities/get-specs", {
            method: "GET",
            mode: 'cors',
            'Access-Control-Allow-Origin': 'http://localhost:80',
            'Access-Control-Max-Age': 3600,
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json',
            },
        })
            .then(result => {
                return result.json();
            })
            .then(res => {
                console.log(res);
                if (res.status === 200) {
                    resolve(res.specs);
                } else {
                    if (res.status === 401) {
                        handleUnauthorized();
                    } else {
                        reject(res.message);
                    }
                }
            })
            .catch(err => {
                console.log('error: ' + err);
                reject(err);
            });
    });
}

export function saveSpec(id, name) {
    return new Promise(function (resolve, reject) {
        authenticatedFetch(Config.apiDomain + "/v1/specialities/save", {
            method: "POST",
            mode: 'cors',
            'Access-Control-Allow-Origin': 'http://localhost:80',
            'Access-Control-Max-Age': 3600,
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                id,
                name,
            })
        })
            .then(result => {
                return result.json();
            })
            .then(res => {
                console.log(res);
                if (res.status === 200) {
                    resolve();
                } else {
                    if (res.status === 401) {
                        handleUnauthorized();
                    } else {
                        reject(res.message || 'Ошибка базы данных!');
                    }
                }
            })
            .catch(err => {
                console.log('error: ' + err);
                reject(err);
            });
    });
}

export function deleteSpec(id) {
    return new Promise(function (resolve, reject) {
        authenticatedFetch(Config.apiDomain + "/v1/specialities/delete-spec?id=" + id, {
            method: "GET",
            mode: 'cors',
            'Access-Control-Allow-Origin': 'http://localhost:80',
            'Access-Control-Max-Age': 3600,
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json',
            },
        })
            .then(result => {
                return result.json();
            })
            .then(res => {
                console.log(res);
                if (res.status === 200) {
                    resolve();
                } else {
                    if (res.status === 401) {
                        handleUnauthorized();
                    } else {
                        reject(res.message || 'Ошибка базы данных!');
                    }
                }
            })
            .catch(err => {
                console.log('error: ' + err);
                reject(err);
            });
    });
}

export function getPermissions() {
    return authenticatedFetch(Config.apiDomain + '/v2/permissions', {
        method: 'GET',
        mode: 'cors',
        headers: {
            'Accept': 'application/json',
        },
    }).then(response => {
        if (response.status === 401) {
            handleUnauthorized();
        }

        return parseV2Response(response, 'Ошибка загрузки разрешений');
    });
}

export function updatePermission(role, permission, enabled) {
    if (typeof enabled !== 'boolean') {
        return Promise.reject(new TypeError('enabled должен быть boolean'));
    }

    return authenticatedFetch(Config.apiDomain + '/v2/permissions', {
        method: 'PATCH',
        mode: 'cors',
        headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({
            role,
            permission,
            enabled
        })
    }).then(response => {
        if (response.status === 401) {
            handleUnauthorized();
        }

        return parseV2Response(response, 'Ошибка сохранения разрешения');
    });
}

export function getMonitors() {
    return new Promise(function (resolve, reject) {
        authenticatedFetch(Config.apiDomain + "/v1/monitor/get-monitors", {
            method: "GET",
            mode: 'cors',
            'Access-Control-Allow-Origin': 'http://localhost:80',
            'Access-Control-Max-Age': 3600,
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json',
            },
        })
            .then(result => {
                return result.json();
            })
            .then(res => {
                console.log(res);
                if (res.status === 200) {
                    resolve(res.monitors);
                } else {
                    if (res.status === 401) {
                        handleUnauthorized();
                    } else {
                        reject(res.message);
                    }
                }
            })
            .catch(err => {
                console.log('error: ' + err);
                reject(err);
            });
    });
}

export function saveMonitor(id, name, description, norm1, norm2, icon, graph_type, enter_type, is_default, unit_id, ios, android, polar, garmin, isTask, isHide, excludeFromPicker, min, max, digit_round, answers = null) {
    return new Promise(function (resolve, reject) {
        const body = {
            id,
            name,
            description,
            norm1,
            norm2,
            icon,
            graph_type,
            enter_type,
            isTask,
            is_default,
            unit_id,
            ios,
            android,
            polar,
            garmin,
            min,
            max,
            isHide,
            excludeFromPicker,
            digit_round,
        };
        if (answers != null) {
            body.answers = answers;
        }
        authenticatedFetch(Config.apiDomain + "/v1/monitor/save", {
            method: "POST",
            mode: 'cors',
            'Access-Control-Allow-Origin': 'http://localhost:80',
            'Access-Control-Max-Age': 3600,
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(body)
        })
            .then(result => {
                return result.json();
            })
            .then(res => {
                console.log(res);
                if (res.status === 200) {
                    resolve();
                } else {
                    if (res.status === 401) {
                        handleUnauthorized();
                    } else {
                        reject(res.message || 'Ошибка базы данных!');
                    }
                }
            })
            .catch(err => {
                console.log('error: ' + err);
                reject(err);
            });
    });
}

export function getRecommendationTypes() {
    return new Promise(function (resolve, reject) {
        authenticatedFetch(Config.apiDomain + "/v1/recommendation/get-recommendation-types", {
            method: "GET",
            mode: 'cors',
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json',
            },
        })
            .then(result => result.json())
            .then(res => {
                if (res.status === 200) {
                    resolve(res.recommendation_types || []);
                } else {
                    if (res.status === 401) {
                        handleUnauthorized();
                    }
                    reject(res.message || 'Ошибка');
                }
            })
            .catch(err => reject(err));
    });
}

export function getHabitsAdmin() {
    return new Promise(function (resolve, reject) {
        authenticatedFetch(Config.apiDomain + "/v1/recommendation/get-habits-admin", {
            method: "GET",
            mode: 'cors',
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json',
            },
        })
            .then(result => result.json())
            .then(res => {
                if (res.status === 200) {
                    resolve(res);
                } else {
                    if (res.status === 401) {
                        handleUnauthorized();
                    }
                    reject(res.message || 'Ошибка');
                }
            })
            .catch(err => reject(err));
    });
}

export function saveHabitAdmin(id, name, description, short_description, monitor_id, priority, is_habit, recommendation_type_id, question_id = null, question_answer_id = null) {
    return new Promise(function (resolve, reject) {
        authenticatedFetch(Config.apiDomain + "/v1/recommendation/save-habit-admin", {
            method: "POST",
            mode: 'cors',
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                id: id || 0,
                name,
                description,
                short_description,
                monitor_id: monitor_id || null,
                priority: parseInt(priority) || 0,
                is_habit: !!is_habit,
                recommendation_type_id: recommendation_type_id != null ? recommendation_type_id : null,
                question_id: question_id != null ? question_id : null,
                question_answer_id: question_answer_id != null ? question_answer_id : null,
            })
        })
            .then(result => result.json())
            .then(res => {
                if (res.status === 200) {
                    resolve(res);
                } else {
                    reject(res.message || 'Ошибка сохранения');
                }
            })
            .catch(err => reject(err));
    });
}

export function deleteHabitAdmin(id) {
    return new Promise(function (resolve, reject) {
        authenticatedFetch(Config.apiDomain + "/v1/recommendation/delete-habit-admin?id=" + id, {
            method: "GET",
            mode: 'cors',
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json',
            },
        })
            .then(result => result.json())
            .then(res => {
                if (res.status === 200) {
                    resolve();
                } else {
                    reject(res.message || 'Ошибка удаления');
                }
            })
            .catch(err => reject(err));
    });
}

export function deleteMonitor(id) {
    return new Promise(function (resolve, reject) {
        authenticatedFetch(Config.apiDomain + "/v1/monitor/delete-monitor?id=" + id, {
            method: "GET",
            mode: 'cors',
            'Access-Control-Allow-Origin': 'http://localhost:80',
            'Access-Control-Max-Age': 3600,
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json',
            },
        })
            .then(result => {
                return result.json();
            })
            .then(res => {
                console.log(res);
                if (res.status === 200) {
                    resolve();
                } else {
                    if (res.status === 401) {
                        handleUnauthorized();
                    } else {
                        reject(res.message || 'Ошибка базы данных!');
                    }
                }
            })
            .catch(err => {
                console.log('error: ' + err);
                reject(err);
            });
    });
}


export function getUsers() {
    return new Promise(function (resolve, reject) {
        authenticatedFetch(Config.apiDomain + "/v1/user/get-users", {
            method: "GET",
            mode: 'cors',
            'Access-Control-Allow-Origin': 'http://localhost:80',
            'Access-Control-Max-Age': 3600,
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json',
            },
        })
            .then(result => {
                return result.json();
            })
            .then(res => {
                console.log(res);
                if (res.status === 200) {
                    resolve(res.users);
                } else {
                    if (res.status === 401) {
                        handleUnauthorized();
                    } else {
                        reject(res.message);
                    }
                }
            })
            .catch(err => {
                console.log('error: ' + err);
                reject(err);
            });
    });
}


export function getUser(userId) {
    return new Promise(function (resolve, reject) {
        authenticatedFetch(Config.apiDomain + "/v1/user/get-user?userId=" + userId, {
            method: "GET",
            mode: 'cors',
            'Access-Control-Allow-Origin': 'http://localhost:80',
            'Access-Control-Max-Age': 3600,
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json',
            },
        })
            .then(result => {
                return result.json();
            })
            .then(res => {
                if (res.status === 200) {
                    resolve(res.user);
                } else {
                    if (res.status === 401) {
                        handleUnauthorized();
                    } else {
                        reject(res.message);
                    }
                }
            })
            .catch(err => {
                console.log('error: ' + err);
                reject(err);
            });
    });
}

export function getUserResearch(userId) {
    return new Promise(function (resolve, reject) {
        authenticatedFetch(Config.apiDomain + "/v1/tasks/get-research-history-by-doctor?pacient_id=" + userId, {
            method: "GET",
            mode: 'cors',
            'Access-Control-Allow-Origin': 'http://localhost:80',
            'Access-Control-Max-Age': 3600,
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json',
            },
        })
            .then(result => {
                return result.json();
            })
            .then(res => {
                if (res.status === 200) {
                    resolve(res);
                } else {
                    if (res.status === 401) {
                        handleUnauthorized();
                    } else {
                        reject(res.message);
                    }
                }
            })
            .catch(err => {
                console.log('error: ' + err);
                reject(err);
            });
    });
}

export function deleteResearchPhoto(research_id, photo_id) {
    return new Promise(function (resolve, reject) {
        let path = '/v1/tasks/delete-research-photo?research_id=' + research_id + '&photo_id=' + photo_id;
        authenticatedFetch(Config.apiDomain + path, {
            method: "GET",
            mode: 'cors',
            'Access-Control-Allow-Origin': 'http://localhost:80',
            'Access-Control-Max-Age': 3600,
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json',
            },
        })
            .then(result => {
                return result.json();
            })
            .then(res => {
                if (res.status === 200) {
                    resolve(res);
                } else {
                    if (res.status === 401) {
                        handleUnauthorized();
                    } else {
                        reject(res.message);
                    }
                }
            })
            .catch(err => {
                console.log('error: ' + err);
                reject(err);
            });
    });
}



export function updateUser(id, fio, email, role, subjects, environment_id) {
    return new Promise(function (resolve, reject) {
        authenticatedFetch(Config.apiDomain + "/v1/user/update-user", {
            method: "POST",
            mode: 'cors',
            'Access-Control-Allow-Origin': 'http://localhost:80',
            'Access-Control-Max-Age': 3600,
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                id,
                fio,
                email,
                role,
                subjects,
                environment_id,
            })
        })
            .then(result => {
                return result.json();
            })
            .then(res => {
                console.log(res);
                if (res.status === 200) {
                    resolve();
                } else {
                    if (res.status === 401) {
                        handleUnauthorized();
                    } else {
                        reject(res.message || 'Ошибка базы данных!');
                    }
                }
            })
            .catch(err => {
                console.log('error: ' + err);
                reject(err);
            });
    });
}

export function newPassword(userId) {
    return new Promise(function (resolve, reject) {
        authenticatedFetch(Config.apiDomain + "/v1/user/new-password?id=" + userId, {
            method: "GET",
            mode: 'cors',
            'Access-Control-Allow-Origin': 'http://localhost:80',
            'Access-Control-Max-Age': 3600,
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json',
            },
        })
            .then(result => {
                return result.json();
            })
            .then(res => {
                console.log(res);
                if (res.status === 200) {
                    resolve();
                } else {
                    if (res.status === 401) {
                        handleUnauthorized();
                    } else {
                        reject(res.message || 'Ошибка базы данных!');
                    }
                }
            })
            .catch(err => {
                console.log('error: ' + err);
                reject(err);
            });
    });
}

export function setUserStatus(userId, status) {
    return new Promise(function (resolve, reject) {
        authenticatedFetch(Config.apiDomain + "/v1/user/set-user-status?id=" + userId + '&status=' + status, {
            method: "GET",
            mode: 'cors',
            'Access-Control-Allow-Origin': 'http://localhost:80',
            'Access-Control-Max-Age': 3600,
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json',
            },
        })
            .then(result => {
                return result.json();
            })
            .then(res => {
                console.log(res);
                if (res.status === 200) {
                    resolve();
                } else {
                    if (res.status === 401) {
                        handleUnauthorized();
                    } else {
                        reject(res.message || 'Ошибка базы данных!');
                    }
                }
            })
            .catch(err => {
                console.log('error: ' + err);
                reject(err);
            });
    });
}

export function deleteUsersHard(userIds, deleteAudit) {
    return authenticatedFetch(Config.apiDomain + '/v2/users', {
        method: 'DELETE',
        mode: 'cors',
        headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({
            user_ids: userIds,
            delete_audit: deleteAudit,
        })
    }).then(response => {
        if (response.status === 401) {
            handleUnauthorized();
        }

        return parseV2Response(response, 'Ошибка удаления пользователей!');
    }).then(body => body.deleted_user_ids || []);
}

export function saveProfile(fio, about, education, service_enabled, service_price, avatar) {

    return new Promise(function (resolve, reject) {

        authenticatedFetch(Config.apiDomain + '/v1/user/update-profile', {
            method: 'post',
            headers: {
                // 'Content-Type': 'multipart/form-data',
                'Accept': 'application/json',
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                fio,
                avatar,
                about,
                education,
                service_price,
                service_enabled,
            })
        }).then(response => {
            response.json().then(data => {
                console.warn('saveProfile: ' + JSON.stringify(data));
                if (data.status == 200) {
                    let user = data.profile;
                    network.userProfile = user;
                    resolve();
                } else {
                    reject(data.message);
                }
            });
        }).catch(err => {
            console.warn(err)
            reject('Неизвестная ошибка. Повторите снова.');
        });
    });
}

export function getLessons(id) {
    return new Promise(function (resolve, reject) {
        authenticatedFetch(Config.apiDomain + "/v1/subjects/get-lessons?id=" + id, {
            method: "GET",
            mode: 'cors',
            'Access-Control-Allow-Origin': 'http://localhost:80',
            'Access-Control-Max-Age': 3600,
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json',
            },
        })
            .then(result => {
                return result.json();
            })
            .then(res => {
                console.log(res);
                if (res.status === 200) {
                    resolve(res.lessons);
                } else {
                    if (res.status === 401) {
                        handleUnauthorized();
                    } else {
                        reject(res.message || 'Ошибка базы данных!');
                    }
                }
            })
            .catch(err => {
                console.log('error: ' + err);
                reject(err);
            });
    });
}

export function saveLesson(id, subject_id, name, description, body, image, paid, show) {
    return new Promise(function (resolve, reject) {
        authenticatedFetch(Config.apiDomain + "/v1/subjects/update-lesson", {
            method: "POST",
            mode: 'cors',
            'Access-Control-Allow-Origin': 'http://localhost:80',
            'Access-Control-Max-Age': 3600,
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                id,
                subject_id,
                name,
                description,
                body,
                image,
                paid,
                show,
            })
        })
            .then(result => {
                return result.json();
            })
            .then(res => {
                console.log(res);
                if (res.status === 200) {
                    resolve();
                } else {
                    if (res.status === 401) {
                        handleUnauthorized();
                    } else {
                        reject(res.message || 'Ошибка базы данных!');
                    }
                }
            })
            .catch(err => {
                console.log('error: ' + err);
                reject(err);
            });
    });
}

export function deleteLesson(id) {
    return new Promise(function (resolve, reject) {
        authenticatedFetch(Config.apiDomain + "/v1/subjects/delete-lesson?id=" + id, {
            method: "GET",
            mode: 'cors',
            'Access-Control-Allow-Origin': 'http://localhost:80',
            'Access-Control-Max-Age': 3600,
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json',
            },
        })
            .then(result => {
                return result.json();
            })
            .then(res => {
                console.log(res);
                if (res.status === 200) {
                    resolve();
                } else {
                    if (res.status === 401) {
                        handleUnauthorized();
                    } else {
                        reject(res.message || 'Ошибка базы данных!');
                    }
                }
            })
            .catch(err => {
                console.log('error: ' + err);
                reject(err);
            });
    });
}

export function getBalls() {
    return new Promise(function (resolve, reject) {
        authenticatedFetch(Config.apiDomain + "/v1/anketa-balls/get-balls", {
            method: "GET",
            mode: 'cors',
            'Access-Control-Allow-Origin': 'http://localhost:80',
            'Access-Control-Max-Age': 3600,
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json',
            },
        })
            .then(result => {
                return result.json();
            })
            .then(res => {
                console.log(res);
                if (res.status === 200) {
                    resolve(res.balls);
                } else {
                    if (res.status === 401) {
                        handleUnauthorized();
                    } else {
                        reject(res.message || 'Ошибка базы данных!');
                    }
                }
            })
            .catch(err => {
                console.log('error: ' + err);
                reject(err);
            });
    });
}

export function saveAnketaBalls(id, part_id, section_id, score1_man, score2_man, score1_woman, score2_woman) {
    return new Promise(function (resolve, reject) {
        authenticatedFetch(Config.apiDomain + "/v1/anketa-balls/save", {
            method: "POST",
            mode: 'cors',
            'Access-Control-Allow-Origin': 'http://localhost:80',
            'Access-Control-Max-Age': 3600,
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                id,
                part_id,
                section_id,
                score1_man,
                score1_woman,
                score2_man,
                score2_woman,
            })
        })
            .then(result => {
                return result.json();
            })
            .then(res => {
                console.log(res);
                if (res.status === 200) {
                    resolve();
                } else {
                    if (res.status === 401) {
                        handleUnauthorized();
                    } else {
                        reject(res.message || 'Ошибка базы данных!');
                    }
                }
            })
            .catch(err => {
                console.log('error: ' + err);
                reject(err);
            });
    });
}

export function deleteBall(id) {
    return new Promise(function (resolve, reject) {
        authenticatedFetch(Config.apiDomain + "/v1/anketa-balls/delete-ball?id=" + id, {
            method: "GET",
            mode: 'cors',
            'Access-Control-Allow-Origin': 'http://localhost:80',
            'Access-Control-Max-Age': 3600,
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json',
            },
        })
            .then(result => {
                return result.json();
            })
            .then(res => {
                console.log(res);
                if (res.status === 200) {
                    resolve();
                } else {
                    if (res.status === 401) {
                        handleUnauthorized();
                    } else {
                        reject(res.message || 'Ошибка базы данных!');
                    }
                }
            })
            .catch(err => {
                console.log('error: ' + err);
                reject(err);
            });
    });
}

export function getDiets() {
    return new Promise(function (resolve, reject) {
        authenticatedFetch(Config.apiDomain + "/v1/diets/get-diets", {
            method: "GET",
            mode: 'cors',
            'Access-Control-Allow-Origin': 'http://localhost:80',
            'Access-Control-Max-Age': 3600,
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json',
            },
        })
            .then(result => {
                return result.json();
            })
            .then(res => {
                console.log(res);
                if (res.status === 200) {
                    resolve(res.diets);
                } else {
                    if (res.status === 401) {
                        handleUnauthorized();
                    } else {
                        reject(res.message || 'Ошибка базы данных!');
                    }
                }
            })
            .catch(err => {
                console.log('error: ' + err);
                reject(err);
            });
    });
}

export function saveDiet(id, name, description, allowed_products, denied_products) {
    return new Promise(function (resolve, reject) {
        authenticatedFetch(Config.apiDomain + "/v1/diets/save", {
            method: "POST",
            mode: 'cors',
            'Access-Control-Allow-Origin': 'http://localhost:80',
            'Access-Control-Max-Age': 3600,
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                id,
                name,
                description,
                allowed_products,
                denied_products,
            })
        })
            .then(result => {
                return result.json();
            })
            .then(res => {
                console.log(res);
                if (res.status === 200) {
                    resolve();
                } else {
                    if (res.status === 401) {
                        handleUnauthorized();
                    } else {
                        reject(res.message || 'Ошибка базы данных!');
                    }
                }
            })
            .catch(err => {
                console.log('error: ' + err);
                reject(err);
            });
    });
}

export function deleteDiet(id) {
    return new Promise(function (resolve, reject) {
        authenticatedFetch(Config.apiDomain + "/v1/diets/delete-diet?id=" + id, {
            method: "GET",
            mode: 'cors',
            'Access-Control-Allow-Origin': 'http://localhost:80',
            'Access-Control-Max-Age': 3600,
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json',
            },
        })
            .then(result => {
                return result.json();
            })
            .then(res => {
                console.log(res);
                if (res.status === 200) {
                    resolve();
                } else {
                    if (res.status === 401) {
                        handleUnauthorized();
                    } else {
                        reject(res.message || 'Ошибка базы данных!');
                    }
                }
            })
            .catch(err => {
                console.log('error: ' + err);
                reject(err);
            });
    });
}

export function getTasks() {
    return new Promise(function (resolve, reject) {
        authenticatedFetch(Config.apiDomain + "/v1/tasks/get-tasks", {
            method: "GET",
            mode: 'cors',
            'Access-Control-Allow-Origin': 'http://localhost:80',
            'Access-Control-Max-Age': 3600,
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json',
            },
        })
            .then(result => {
                return result.json();
            })
            .then(res => {
                console.log(res);
                if (res.status === 200) {
                    resolve(res.tasks);
                } else {
                    if (res.status === 401) {
                        handleUnauthorized();
                    } else {
                        reject(res.message);
                    }
                }
            })
            .catch(err => {
                console.log('error: ' + err);
                reject(err);
            });
    });
}

export function saveTask(id, name, type, title, subtitle, description, icon) {
    return new Promise(function (resolve, reject) {
        authenticatedFetch(Config.apiDomain + "/v1/tasks/save", {
            method: "POST",
            mode: 'cors',
            'Access-Control-Allow-Origin': 'http://localhost:80',
            'Access-Control-Max-Age': 3600,
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                id,
                name,
                type,
                title,
                subtitle,
                description,
                icon,
            })
        })
            .then(result => {
                return result.json();
            })
            .then(res => {
                console.log(res);
                if (res.status === 200) {
                    resolve();
                } else {
                    if (res.status === 401) {
                        handleUnauthorized();
                    } else {
                        reject(res.message || 'Ошибка базы данных!');
                    }
                }
            })
            .catch(err => {
                console.log('error: ' + err);
                reject(err);
            });
    });
}

export function deleteTask(id) {
    return new Promise(function (resolve, reject) {
        authenticatedFetch(Config.apiDomain + "/v1/tasks/delete-task?id=" + id, {
            method: "GET",
            mode: 'cors',
            'Access-Control-Allow-Origin': 'http://localhost:80',
            'Access-Control-Max-Age': 3600,
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json',
            },
        })
            .then(result => {
                return result.json();
            })
            .then(res => {
                console.log(res);
                if (res.status === 200) {
                    resolve();
                } else {
                    if (res.status === 401) {
                        handleUnauthorized();
                    } else {
                        reject(res.message || 'Ошибка базы данных!');
                    }
                }
            })
            .catch(err => {
                console.log('error: ' + err);
                reject(err);
            });
    });
}

export function getDoctors() {
    return new Promise(function (resolve, reject) {
        authenticatedFetch(Config.apiDomain + "/v1/user/get-doctors", {
            method: "GET",
            mode: 'cors',
            'Access-Control-Allow-Origin': 'http://localhost:80',
            'Access-Control-Max-Age': 3600,
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json',
            },
        })
            .then(result => {
                return result.json();
            })
            .then(res => {
                console.log(res);
                if (res.status === 200) {
                    // network.activeDoctors = res.active;
                    // network.newDoctors = res.new;
                    resolve(res);
                } else {
                    if (res.status === 401) {
                        handleUnauthorized();
                    } else {
                        reject(res.message || 'Ошибка базы данных!');
                    }
                }
            })
            .catch(err => {
                console.log('error: ' + err);
                reject(err);
            });
    });
}

export function saveDoctor(id, status, education, dop_education, special_text, isSuper, environment_id, password, avatar, docs) {
    return new Promise(function (resolve, reject) {
        authenticatedFetch(Config.apiDomain + "/v1/user/save-doctor-by-admin", {
            method: "POST",
            mode: 'cors',
            'Access-Control-Allow-Origin': 'http://localhost:80',
            'Access-Control-Max-Age': 3600,
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                id,
                status,
                education,
                dop_education,
                special_text,
                isSuper,
                environment_id,
                password,
                avatar,
                docs
            })
        })
            .then(result => {
                return result.json();
            })
            .then(res => {
                console.log(res);
                if (res.status === 200) {
                    resolve();
                } else {
                    if (res.status === 401) {
                        handleUnauthorized();
                    } else {
                        reject(res.message || 'Ошибка базы данных!');
                    }
                }
            })
            .catch(err => {
                console.log('error: ' + err);
                reject(err);
            });
    });
}

export function createDoctor(fio, email, phone, sex, birthday, workplace, stage, degree_id, rank_id, specs, status, education, dop_education, special_text, isSuper, environment_id, password, avatar, docs) {
    return new Promise(function (resolve, reject) {
        authenticatedFetch(Config.apiDomain + "/v1/user/create-doctor-by-admin", {
            method: "POST",
            mode: 'cors',
            'Access-Control-Allow-Origin': 'http://localhost:80',
            'Access-Control-Max-Age': 3600,
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                fio,
                email,
                phone,
                sex,
                birthday,
                workplace,
                stage,
                degree_id,
                rank_id,
                specs,
                status,
                education,
                dop_education,
                special_text,
                isSuper,
                environment_id,
                password,
                avatar,
                docs
            })
        })
            .then(result => {
                return result.json();
            })
            .then(res => {
                console.log(res);
                if (res.status === 200) {
                    resolve();
                } else {
                    if (res.status === 401) {
                        handleUnauthorized();
                    } else {
                        reject(res.message || 'Ошибка базы данных!');
                    }
                }
            })
            .catch(err => {
                console.log('error: ' + err);
                reject(err);
            });
    });
}

export function getDegrees() {
    return new Promise(function (resolve, reject) {
        authenticatedFetch(Config.apiDomain + "/v1/api/get-degree", {
            method: "GET",
            mode: 'cors',
            'Access-Control-Allow-Origin': 'http://localhost:80',
            'Access-Control-Max-Age': 3600,
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json',
            },
        })
            .then(result => {
                return result.json();
            })
            .then(res => {
                console.log(res);
                if (res.status === 200) {
                    // network.activeDoctors = res.active;
                    // network.newDoctors = res.new;
                    resolve(res);
                } else {
                    if (res.status === 401) {
                        handleUnauthorized();
                    } else {
                        reject(res.message || 'Ошибка базы данных!');
                    }
                }
            })
            .catch(err => {
                console.log('error: ' + err);
                reject(err);
            });
    });
}

export function getDoctorCategories() {
    return new Promise(function (resolve, reject) {
        authenticatedFetch(Config.apiDomain + "/v1/api/get-category", {
            method: "GET",
            mode: 'cors',
            'Access-Control-Allow-Origin': 'http://localhost:80',
            'Access-Control-Max-Age': 3600,
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json',
            },
        })
            .then(result => {
                return result.json();
            })
            .then(res => {
                console.log(res);
                if (res.status === 200) {
                    // network.activeDoctors = res.active;
                    // network.newDoctors = res.new;
                    resolve(res);
                } else {
                    if (res.status === 401) {
                        handleUnauthorized();
                    } else {
                        reject(res.message || 'Ошибка базы данных!');
                    }
                }
            })
            .catch(err => {
                console.log('error: ' + err);
                reject(err);
            });
    });
}

export function getSpecializations() {
    return new Promise(function (resolve, reject) {
        authenticatedFetch(Config.apiDomain + "/v1/api/get-specs", {
            method: "GET",
            mode: 'cors',
            'Access-Control-Allow-Origin': 'http://localhost:80',
            'Access-Control-Max-Age': 3600,
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json',
            },
        })
            .then(result => {
                return result.json();
            })
            .then(res => {
                console.log(res);
                if (res.status === 200) {
                    // network.activeDoctors = res.active;
                    // network.newDoctors = res.new;
                    resolve(res);
                } else {
                    if (res.status === 401) {
                        handleUnauthorized();
                    } else {
                        reject(res.message || 'Ошибка базы данных!');
                    }
                }
            })
            .catch(err => {
                console.log('error: ' + err);
                reject(err);
            });
    });
}

export function getAnalisysGroup() {
    return new Promise(function (resolve, reject) {
        authenticatedFetch(Config.apiDomain + "/v1/analisys-group/get-analisys-group", {
            method: "GET",
            mode: 'cors',
            'Access-Control-Allow-Origin': 'http://localhost:80',
            'Access-Control-Max-Age': 3600,
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json',
            },
        })
            .then(result => {
                return result.json();
            })
            .then(res => {
                console.log(res);
                if (res.status === 200) {
                    resolve(res.groups);
                } else {
                    if (res.status === 401) {
                        handleUnauthorized();
                    } else {
                        reject(res.message);
                    }
                }
            })
            .catch(err => {
                console.log('error: ' + err);
                reject(err);
            });
    });
}

export function saveAnalisGroup(id, name) {
    return new Promise(function (resolve, reject) {
        authenticatedFetch(Config.apiDomain + "/v1/analisys-group/save", {
            method: "POST",
            mode: 'cors',
            'Access-Control-Allow-Origin': 'http://localhost:80',
            'Access-Control-Max-Age': 3600,
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                id,
                name,
            })
        })
            .then(result => {
                return result.json();
            })
            .then(res => {
                console.log(res);
                if (res.status === 200) {
                    resolve();
                } else {
                    if (res.status === 401) {
                        handleUnauthorized();
                    } else {
                        reject(res.message || 'Ошибка базы данных!');
                    }
                }
            })
            .catch(err => {
                console.log('error: ' + err);
                reject(err);
            });
    });
}

export function deleteAnalisysGroup(id) {
    return new Promise(function (resolve, reject) {
        authenticatedFetch(Config.apiDomain + "/v1/analisys-group/delete-analisys-group?id=" + id, {
            method: "GET",
            mode: 'cors',
            'Access-Control-Allow-Origin': 'http://localhost:80',
            'Access-Control-Max-Age': 3600,
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json',
            },
        })
            .then(result => {
                return result.json();
            })
            .then(res => {
                console.log(res);
                if (res.status === 200) {
                    resolve();
                } else {
                    if (res.status === 401) {
                        handleUnauthorized();
                    } else {
                        reject(res.message || 'Ошибка базы данных!');
                    }
                }
            })
            .catch(err => {
                console.log('error: ' + err);
                reject(err);
            });
    });
}

export function setAnalisysGroupOrder(orders) {
    return new Promise(function (resolve, reject) {
        authenticatedFetch(Config.apiDomain + "/v1/analisys-group/set-analis-order", {
            method: "POST",
            mode: 'cors',
            'Access-Control-Allow-Origin': 'http://localhost:80',
            'Access-Control-Max-Age': 3600,
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                orders,
            }),
        })
            .then(result => {
                return result.json();
            })
            .then(res => {
                console.log(res);
                if (res.status === 200) {
                    resolve();
                } else {
                    if (res.status === 401) {
                        handleUnauthorized();
                    } else {
                        reject(res.message || 'Ошибка базы данных!');
                    }
                }
            })
            .catch(err => {
                console.log('error: ' + err);
                reject(err);
            });
    });
}

export function getAnalisysFromGroup(group_id) {
    return new Promise(function (resolve, reject) {
        authenticatedFetch(Config.apiDomain + "/v1/analisys-group/get-analisys-from-group?group_id=" + group_id, {
            method: "GET",
            mode: 'cors',
            'Access-Control-Allow-Origin': 'http://localhost:80',
            'Access-Control-Max-Age': 3600,
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json',
            },
        })
            .then(result => {
                return result.json();
            })
            .then(res => {
                console.log(res);
                if (res.status === 200) {
                    resolve(res.analisys);
                } else {
                    if (res.status === 401) {
                        handleUnauthorized();
                    } else {
                        reject(res.message);
                    }
                }
            })
            .catch(err => {
                console.log('error: ' + err);
                reject(err);
            });
    });
}

export function setAnalisysOrder(orders) {
    return new Promise(function (resolve, reject) {
        authenticatedFetch(Config.apiDomain + "/v1/analis/set-analis-order", {
            method: "POST",
            mode: 'cors',
            'Access-Control-Allow-Origin': 'http://localhost:80',
            'Access-Control-Max-Age': 3600,
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                orders,
            }),
        })
            .then(result => {
                return result.json();
            })
            .then(res => {
                console.log(res);
                if (res.status === 200) {
                    resolve();
                } else {
                    if (res.status === 401) {
                        handleUnauthorized();
                    } else {
                        reject(res.message || 'Ошибка базы данных!');
                    }
                }
            })
            .catch(err => {
                console.log('error: ' + err);
                reject(err);
            });
    });
}

export function getPromocodes() {
    return new Promise(function (resolve, reject) {
        authenticatedFetch(Config.apiDomain + "/v1/transactions/get-promocodes", {
            method: "GET",
            mode: 'cors',
            'Access-Control-Allow-Origin': 'http://localhost:80',
            'Access-Control-Max-Age': 3600,
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json',
            },
        })
            .then(result => {
                return result.json();
            })
            .then(res => {
                console.log(res);
                if (res.status === 200) {
                    resolve(res);
                } else {
                    if (res.status === 401) {
                        handleUnauthorized();
                    } else {
                        reject(res.message || 'Ошибка базы данных!');
                    }
                }
            })
            .catch(err => {
                console.log('error: ' + err);
                reject(err);
            });
    });
}

export function generatePromocode() {
    return new Promise(function (resolve, reject) {
        authenticatedFetch(Config.apiDomain + "/v1/transactions/generate-promocode", {
            method: "GET",
            mode: 'cors',
            'Access-Control-Allow-Origin': 'http://localhost:80',
            'Access-Control-Max-Age': 3600,
            headers: {
            },
        })
            .then(result => {
                return result.json();
            })
            .then(res => {
                console.log(res);
                if (res.status === 200) {
                    resolve();
                } else {
                    if (res.status === 401) {
                        handleUnauthorized();
                    } else {
                        reject(res.message || 'Ошибка базы данных!');
                    }
                }
            })
            .catch(err => {
                console.log('error: ' + err);
                reject(err);
            });
    });
}

export function uploadAnalyzes(formData) {
    return new Promise(function (resolve, reject) {
        authenticatedFetch(Config.apiDomain + "/v1/import/analyzes", {
            method: "POST",
            mode: 'cors',
            'Access-Control-Allow-Origin': 'http://localhost:80',
            'Access-Control-Max-Age': 3600,
            headers: {
            },
            body: formData,
        })
            .then(result => {
                return result.json();
            })
            .then(res => {
                if (typeof res.status !== 'undefined' && res.status === 401) {
                    handleUnauthorized();
                }

                if (res.error) {
                    reject(res.error);
                }
                else {
                    resolve(res);
                }
            })
            .catch(err => {
                console.log('error: ' + err);
                reject(err);
            });
    });
}

export function uploadResearch(formData) {
    return new Promise(function (resolve, reject) {
        authenticatedFetch(Config.apiDomain + "/v1/import/research", {
            method: "POST",
            mode: 'cors',
            'Access-Control-Allow-Origin': 'http://localhost:80',
            'Access-Control-Max-Age': 3600,
            headers: {
            },
            body: formData,
        })
            .then(result => {
                return result.json();
            })
            .then(res => {
                if (typeof res.status !== 'undefined' && res.status === 401) {
                    handleUnauthorized();
                }
                if (res.error) {
                    reject(res.error);
                }
                else {
                    resolve(res);
                }
            })
            .catch(err => {
                console.log('error: ' + err);
                reject(err);
            });
    });
}

export function importAnalyzes(fileName) {
    return new Promise(function (resolve, reject) {
        authenticatedFetch(Config.apiDomain + "/v1/import/analyzes-import", {
            method: "POST",
            mode: 'cors',
            'Access-Control-Allow-Origin': 'http://localhost:80',
            'Access-Control-Max-Age': 3600,
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                file: fileName,
            }),
        })
            .then(result => {
                return result.json();
            })
            .then(res => {
                if (res.error) {
                    reject(res.error);
                }
                else {
                    resolve(res);
                }
            })
            .catch(err => {
                console.log('error: ' + err);
                reject(err);
            });
    });
}

export function getAuthCodes(q) {
    const query = q && String(q).trim() ? '?q=' + encodeURIComponent(q.trim()) : '';
    return new Promise(function (resolve, reject) {
        authenticatedFetch(Config.apiDomain + "/v1/user/get-auth-codes" + query, {
            method: "GET",
            mode: 'cors',
            'Access-Control-Allow-Origin': 'http://localhost:80',
            'Access-Control-Max-Age': 3600,
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json',
            },
        })
            .then(result => result.json())
            .then(res => {
                if (res.status === 200) {
                    resolve(res.items || []);
                } else {
                    if (res.status === 401) {
                        handleUnauthorized();
                    } else {
                        reject(res.message || 'Ошибка загрузки');
                    }
                }
            })
            .catch(err => {
                console.log('error: ' + err);
                reject(err);
            });
    });
}

export function getEnvironments() {
    return new Promise(function (resolve, reject) {
        authenticatedFetch(Config.apiDomain + "/v1/environments/get-list", {
            method: "GET",
            mode: 'cors',
            'Access-Control-Allow-Origin': 'http://localhost:80',
            'Access-Control-Max-Age': 3600,
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json',
            },
        })
            .then(result => {
                return result.json();
            })
            .then(res => {
                if (res.status === 200) {
                    resolve(res.environments);
                } else {
                    if (res.status === 401) {
                        handleUnauthorized();
                    } else {
                        reject(res.message);
                    }
                }
            })
            .catch(err => {
                console.log('error: ' + err);
                reject(err);
            });
    });
}

export function addEnvironment(data) {
    return new Promise(function (resolve, reject) {
        authenticatedFetch(Config.apiDomain + "/v1/environments/add", {
            method: "POST",
            mode: 'cors',
            'Access-Control-Allow-Origin': 'http://localhost:80',
            'Access-Control-Max-Age': 3600,
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(data)
        })
            .then(result => {
                return result.json();
            })
            .then(res => {
                if (res.status === 200) {
                    if (res.success) {
                        resolve(res.environments);
                    } else {
                        reject(res.errors)
                    }
                } else {
                    if (res.status === 401) {
                        handleUnauthorized();
                    } else {
                        reject(res.message || 'Ошибка базы данных!');
                    }
                }
            })
            .catch(err => {
                console.log('error: ' + err);
                reject(err);
            });
    });
}

export function editEnvironment(id, data) {
    data.id = id;

    return new Promise(function (resolve, reject) {
        authenticatedFetch(Config.apiDomain + "/v1/environments/edit", {
            method: "POST",
            mode: 'cors',
            'Access-Control-Allow-Origin': 'http://localhost:80',
            'Access-Control-Max-Age': 3600,
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(data)
        })
            .then(result => {
                return result.json();
            })
            .then(res => {
                if (res.status === 200) {
                    if (res.success) {
                        resolve(res.environments);
                    } else {
                        reject(res.errors)
                    }
                } else {
                    if (res.status === 401) {
                        handleUnauthorized();
                    } else {
                        reject(res.message || 'Ошибка базы данных!');
                    }
                }
            })
            .catch(err => {
                console.log('error: ' + err);
                reject(err);
            });
    });
}

export function deleteEnvironment(id) {
    return new Promise(function (resolve, reject) {
        authenticatedFetch(Config.apiDomain + "/v1/environments/delete-env?id=" + id, {
            method: "GET",
            mode: 'cors',
            'Access-Control-Allow-Origin': 'http://localhost:80',
            'Access-Control-Max-Age': 3600,
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json',
            },
        })
            .then(result => {
                return result.json();
            })
            .then(res => {
                if (res.status === 200) {
                    if (res.success) {
                        resolve(res.environments);
                    } else {
                        reject(res.errors)
                    }
                } else {
                    if (res.status === 401) {
                        handleUnauthorized();
                    } else {
                        reject(res.message || 'Ошибка базы данных!');
                    }
                }
            })
            .catch(err => {
                console.log('error: ' + err);
                reject(err);
            });
    });
}

export function downloadLog(id, userId, date) {
    return new Promise(function (resolve, reject) {
        authenticatedFetch(Config.apiDomain + "/v1/user/download-log?id=" + id, {
            method: "GET",
            mode: 'cors',
            'Access-Control-Allow-Origin': 'http://localhost:80',
            'Access-Control-Max-Age': 3600,
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json',
            },
        })
            .then(result => {
                return result.blob();
            })
            .then(blob => {
                const url = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.download = userId + '-' + date + '.zip';
                document.body.appendChild(link);
                link.click();
                link.remove();
                window.URL.revokeObjectURL(url);
                resolve();
            })
            .catch(err => {
                console.log('error: ' + err);
                reject(err);
            });
    });
}

export function getAvatarSettings() {
    return new Promise(function (resolve, reject) {
        authenticatedFetch(Config.apiDomain + "/v1/settings/avatar", {
            method: "GET",
            mode: 'cors',
            'Access-Control-Allow-Origin': 'http://localhost:80',
            'Access-Control-Max-Age': 3600,
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json',
            },
        })
            .then(result => {
                return result.json();
            })
            .then(res => {
                console.log(res);
                if (res.status === 200) {
                    resolve(res.data || { disable_avatar_without_accompaniment: 0 });
                } else {
                    if (res.status === 401) {
                        handleUnauthorized();
                    } else {
                        reject(res.message || 'Ошибка базы данных!');
                    }
                }
            })
            .catch(err => {
                console.log('error: ' + err);
                reject(err);
            });
    });
}

export function saveAvatarSettings(settings) {
    return new Promise(function (resolve, reject) {
        authenticatedFetch(Config.apiDomain + "/v1/settings/avatar", {
            method: "POST",
            mode: 'cors',
            'Access-Control-Allow-Origin': 'http://localhost:80',
            'Access-Control-Max-Age': 3600,
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(settings)
        })
            .then(result => {
                return result.json();
            })
            .then(res => {
                console.log(res);
                if (res.status === 200) {
                    resolve();
                } else {
                    if (res.status === 401) {
                        handleUnauthorized();
                    } else {
                        reject(res.message || 'Ошибка базы данных!');
                    }
                }
            })
            .catch(err => {
                console.log('error: ' + err);
                reject(err);
            });
    });
}

export function getProtocolSettings() {
    return new Promise(function (resolve, reject) {
        authenticatedFetch(Config.apiDomain + "/v2/protocol-settings", {
            method: "GET",
            mode: 'cors',
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json',
            },
        })
            .then(result => {
                if (result.status === 401) { handleUnauthorized(); reject('Unauthorized'); return; }
                if (!result.ok) { return result.json().then(e => Promise.reject(e.message || 'Ошибка')); }
                return result.json();
            })
            .then(data => { if (data) resolve(data); })
            .catch(err => reject(err));
    });
}

export function saveProtocolSettings(payload) {
    return new Promise(function (resolve, reject) {
        authenticatedFetch(Config.apiDomain + "/v2/protocol-settings", {
            method: "POST",
            mode: 'cors',
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(payload)
        })
            .then(result => {
                if (result.status === 401) { handleUnauthorized(); reject('Unauthorized'); return; }
                if (!result.ok) { return result.json().then(e => Promise.reject(e.message || 'Ошибка')); }
                resolve();
            })
            .catch(err => reject(err));
    });
}

export function getSmsSettings() {
    return new Promise(function (resolve, reject) {
        authenticatedFetch(Config.apiDomain + "/v2/sms-settings", {
            method: "GET",
            mode: 'cors',
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json',
            },
        })
            .then(result => {
                if (result.status === 401) { handleUnauthorized(); reject('Unauthorized'); return; }
                if (!result.ok) { return result.json().then(e => Promise.reject(e.message || 'Ошибка')); }
                return result.json();
            })
            .then(data => { if (data) resolve(data); })
            .catch(err => reject(err));
    });
}

export function saveSmsSettings(payload) {
    return new Promise(function (resolve, reject) {
        authenticatedFetch(Config.apiDomain + "/v2/sms-settings", {
            method: "POST",
            mode: 'cors',
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(payload)
        })
            .then(result => {
                if (result.status === 401) { handleUnauthorized(); reject('Unauthorized'); return; }
                if (!result.ok) { return result.json().then(e => Promise.reject(e.message || 'Ошибка')); }
                resolve();
            })
            .catch(err => reject(err));
    });
}

export function getDoctorCabinetLogoSettings() {
    return new Promise(function (resolve, reject) {
        authenticatedFetch(Config.apiDomain + "/v1/settings/doctor-cabinet-logo", {
            method: "GET",
            mode: 'cors',
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json',
            },
        })
            .then(result => result.json())
            .then(res => {
                if (res.status === 200 && res.data) {
                    resolve(res.data);
                } else {
                    if (res.status === 401) {
                        handleUnauthorized();
                    } else {
                        reject(res.message || 'Ошибка загрузки настроек логотипа');
                    }
                }
            })
            .catch(err => {
                console.log('error: ' + err);
                reject(err);
            });
    });
}

export function saveDoctorCabinetLogoUrl(logoUrl) {
    return new Promise(function (resolve, reject) {
        authenticatedFetch(Config.apiDomain + "/v1/settings/doctor-cabinet-logo", {
            method: "POST",
            mode: 'cors',
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({ logo_url: logoUrl === undefined || logoUrl === null ? '' : String(logoUrl) })
        })
            .then(result => result.json())
            .then(res => {
                if (res.status === 200) {
                    resolve(res.data || {});
                } else {
                    if (res.status === 401) {
                        handleUnauthorized();
                    } else {
                        reject(res.message || 'Ошибка сохранения');
                    }
                }
            })
            .catch(err => {
                console.log('error: ' + err);
                reject(err);
            });
    });
}

export function uploadDoctorCabinetLogo(file) {
    return new Promise(function (resolve, reject) {
        const formData = new FormData();
        formData.append('logo', file);
        authenticatedFetch(Config.apiDomain + "/v1/settings/doctor-cabinet-logo", {
            method: "POST",
            mode: 'cors',
            headers: {
                'Accept': 'application/json',
            },
            body: formData
        })
            .then(result => result.json())
            .then(res => {
                if (res.status === 200) {
                    resolve(res.data || {});
                } else {
                    if (res.status === 401) {
                        handleUnauthorized();
                    } else {
                        reject(res.message || 'Ошибка загрузки файла');
                    }
                }
            })
            .catch(err => {
                console.log('error: ' + err);
                reject(err);
            });
    });
}

export function getProtocolLogoSettings() {
    return new Promise(function (resolve, reject) {
        authenticatedFetch(Config.apiDomain + "/v1/settings/protocol-logo", {
            method: "GET",
            mode: 'cors',
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json',
            },
        })
            .then(result => result.json())
            .then(res => {
                if (res.status === 200 && res.data) {
                    resolve(res.data);
                } else {
                    if (res.status === 401) {
                        handleUnauthorized();
                    } else {
                        reject(res.message || 'Ошибка загрузки настроек логотипа протокола');
                    }
                }
            })
            .catch(err => {
                console.log('error: ' + err);
                reject(err);
            });
    });
}

export function saveProtocolLogoUrl(logoUrl) {
    return new Promise(function (resolve, reject) {
        authenticatedFetch(Config.apiDomain + "/v1/settings/protocol-logo", {
            method: "POST",
            mode: 'cors',
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({ logo_url: logoUrl === undefined || logoUrl === null ? '' : String(logoUrl) })
        })
            .then(result => result.json())
            .then(res => {
                if (res.status === 200) {
                    resolve(res.data || {});
                } else {
                    if (res.status === 401) {
                        handleUnauthorized();
                    } else {
                        reject(res.message || 'Ошибка сохранения');
                    }
                }
            })
            .catch(err => {
                console.log('error: ' + err);
                reject(err);
            });
    });
}

export function uploadProtocolLogo(file) {
    return new Promise(function (resolve, reject) {
        const formData = new FormData();
        formData.append('logo', file);
        authenticatedFetch(Config.apiDomain + "/v1/settings/protocol-logo", {
            method: "POST",
            mode: 'cors',
            headers: {
                'Accept': 'application/json',
            },
            body: formData
        })
            .then(result => result.json())
            .then(res => {
                if (res.status === 200) {
                    resolve(res.data || {});
                } else {
                    if (res.status === 401) {
                        handleUnauthorized();
                    } else {
                        reject(res.message || 'Ошибка загрузки файла');
                    }
                }
            })
            .catch(err => {
                console.log('error: ' + err);
                reject(err);
            });
    });
}

export function getPatientAvatarAccess(patientId) {
    return new Promise(function (resolve, reject) {
        authenticatedFetch(Config.apiDomain + "/v1/admin/patient/" + patientId + "/avatar/access", {
            method: "GET",
            mode: 'cors',
            'Access-Control-Allow-Origin': 'http://localhost:80',
            'Access-Control-Max-Age': 3600,
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json',
            },
        })
            .then(result => {
                return result.json();
            })
            .then(res => {
                console.log(res);
                if (res.status === 200) {
                    resolve(res.data || { has_access: false, expires_at: null });
                } else {
                    if (res.status === 401) {
                        handleUnauthorized();
                    } else if (res.status === 404) {
                        // Если доступа нет, это нормально - возвращаем false
                        resolve({ has_access: false, expires_at: null });
                    } else {
                        reject(res.message || 'Ошибка базы данных!');
                    }
                }
            })
            .catch(err => {
                console.log('error: ' + err);
                // При ошибке считаем, что доступа нет
                resolve({ has_access: false, expires_at: null });
            });
    });
}

export function savePatientAvatarAccess(patientId, enabled) {
    return new Promise(function (resolve, reject) {
        authenticatedFetch(Config.apiDomain + "/v1/admin/patient/" + patientId + "/avatar/access", {
            method: "POST",
            mode: 'cors',
            'Access-Control-Allow-Origin': 'http://localhost:80',
            'Access-Control-Max-Age': 3600,
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                enabled: enabled
            })
        })
            .then(result => {
                return result.json();
            })
            .then(res => {
                console.log(res);
                if (res.status === 200) {
                    resolve(res.data || {});
                } else {
                    if (res.status === 401) {
                        handleUnauthorized();
                    } else {
                        reject(res.message || 'Ошибка базы данных!');
                    }
                }
            })
            .catch(err => {
                console.log('error: ' + err);
                reject(err);
            });
    });
}

export function getTasksGoalsReminderSettings() {
    return new Promise(function (resolve, reject) {
        authenticatedFetch(Config.apiDomain + "/v1/settings/tasks-goals-reminder", {
            method: "GET",
            mode: 'cors',
            'Access-Control-Allow-Origin': 'http://localhost:80',
            'Access-Control-Max-Age': 3600,
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json',
            },
        })
            .then(result => {
                return result.json();
            })
            .then(res => {
                console.log(res);
                if (res.status === 200 && res.success) {
                    resolve(res.data || {
                        tasks_goals_reminder_text: '',
                        tasks_goals_reminder_interval_minutes: 10080,
                        tasks_goals_reminder_min_avatar_completion_percent: 0
                    });
                } else {
                    if (res.status === 401) {
                        handleUnauthorized();
                    } else {
                        if (res.hasOwnProperty('code')) {
                            reject('Ошибка базы данных!');
                        } else {
                            reject(res.message || 'Ошибка загрузки настроек');
                        }
                    }
                }
            })
            .catch(err => {
                console.log('error: ' + err);
                reject(err);
            });
    });
}

export function getComplaintsGoalsOnboardingSettings() {
    return new Promise(function (resolve, reject) {
        authenticatedFetch(Config.apiDomain + "/v1/settings/complaints-goals-onboarding", {
            method: "GET",
            mode: 'cors',
            'Access-Control-Allow-Origin': 'http://localhost:80',
            'Access-Control-Max-Age': 3600,
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json',
            },
        })
            .then(result => result.json())
            .then(res => {
                if (res.status === 200 && res.success) {
                    resolve(res.data || {
                        onboarding_complaints_enabled: 1,
                        onboarding_goals_enabled: 1,
                        onboarding_delayed_enabled: 0,
                        onboarding_delay_hours: 24,
                        onboarding_delayed_push_text:
                            'Хотите рекомендации по здоровому образу жизни прямо сейчас? Заполните дополнительную информацию о себе',
                    });
                } else {
                    if (res.status === 401) {
                        handleUnauthorized();
                    } else {
                        reject(res.message || 'Ошибка загрузки настроек');
                    }
                }
            })
            .catch(err => reject(err));
    });
}

export function saveComplaintsGoalsOnboardingSettings(settings) {
    return new Promise(function (resolve, reject) {
        authenticatedFetch(Config.apiDomain + "/v1/settings/complaints-goals-onboarding", {
            method: "POST",
            mode: 'cors',
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                onboarding_complaints_enabled: settings.onboarding_complaints_enabled,
                onboarding_goals_enabled: settings.onboarding_goals_enabled,
                onboarding_delayed_enabled: settings.onboarding_delayed_enabled,
                onboarding_delay_hours: settings.onboarding_delay_hours,
                onboarding_delayed_push_text: settings.onboarding_delayed_push_text,
            })
        })
            .then(result => result.json())
            .then(res => {
                if (res.status === 200 && res.success) {
                    resolve();
                } else {
                    if (res.status === 401) {
                        handleUnauthorized();
                    } else {
                        reject(res.message || 'Ошибка сохранения');
                    }
                }
            })
            .catch(err => reject(err));
    });
}

export function saveTasksGoalsReminderSettings(settings) {
    return new Promise(function (resolve, reject) {
        authenticatedFetch(Config.apiDomain + "/v1/settings/tasks-goals-reminder", {
            method: "POST",
            mode: 'cors',
            'Access-Control-Allow-Origin': 'http://localhost:80',
            'Access-Control-Max-Age': 3600,
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                tasks_goals_reminder_text: settings.tasks_goals_reminder_text,
                tasks_goals_reminder_interval_minutes: settings.tasks_goals_reminder_interval_minutes,
                tasks_goals_reminder_min_avatar_completion_percent: settings.tasks_goals_reminder_min_avatar_completion_percent
            })
        })
            .then(result => {
                return result.json();
            })
            .then(res => {
                console.log(res);
                if (res.status === 200 && res.success) {
                    resolve();
                } else {
                    if (res.status === 401) {
                        handleUnauthorized();
                    } else {
                        if (res.hasOwnProperty('code')) {
                            reject('Ошибка базы данных!');
                        } else {
                            reject(res.message || 'Ошибка сохранения настроек');
                        }
                    }
                }
            })
            .catch(err => {
                console.log('error: ' + err);
                reject(err);
            });
    });
}

export function getEmergencyHelpSettings() {
    return new Promise(function (resolve, reject) {
        authenticatedFetch(Config.apiDomain + "/v1/settings/emergency-help", {
            method: "GET",
            mode: 'cors',
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json',
            },
        })
            .then(result => result.json())
            .then(res => {
                if (res.status === 200 && res.success && res.data) {
                    resolve(res.data);
                } else {
                    if (res.status === 401) {
                        handleUnauthorized();
                    }
                    reject(res.message || 'Ошибка загрузки настройки «Самопомощь»');
                }
            })
            .catch(err => reject(err));
    });
}

export function saveEmergencyHelpSettings(enabled) {
    return new Promise(function (resolve, reject) {
        authenticatedFetch(Config.apiDomain + "/v1/settings/emergency-help", {
            method: "POST",
            mode: 'cors',
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({ enabled: enabled ? 1 : 0 })
        })
            .then(result => result.json())
            .then(res => {
                if (res.status === 200 && res.success) {
                    resolve();
                } else {
                    if (res.status === 401) {
                        handleUnauthorized();
                    }
                    reject(res.message || 'Ошибка сохранения настройки «Самопомощь»');
                }
            })
            .catch(err => reject(err));
    });
}

export function getHabitsSettings() {
    return new Promise(function (resolve, reject) {
        authenticatedFetch(Config.apiDomain + "/v1/settings/habits-settings", {
            method: "GET",
            mode: 'cors',
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json',
            },
        })
            .then(result => result.json())
            .then(res => {
                if (res.status === 200 && res.success && res.data) {
                    resolve(res.data);
                } else {
                    if (res.status === 401) {
                        handleUnauthorized();
                    }
                    reject(res.message || 'Ошибка загрузки настроек привычек');
                }
            })
            .catch(err => reject(err));
    });
}

export function saveHabitsSettings(settings) {
    return new Promise(function (resolve, reject) {
        authenticatedFetch(Config.apiDomain + "/v1/settings/habits-settings", {
            method: "POST",
            mode: 'cors',
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(settings)
        })
            .then(result => result.json())
            .then(res => {
                if (res.status === 200 && res.success) {
                    resolve();
                } else {
                    if (res.status === 401) {
                        handleUnauthorized();
                    }
                    reject(res.message || 'Ошибка сохранения настроек привычек');
                }
            })
            .catch(err => reject(err));
    });
}

export function getSystemChatAdminSettings() {
    return new Promise(function (resolve, reject) {
        authenticatedFetch(Config.apiDomain + "/v1/settings/system-chat-admin", {
            method: "GET",
            mode: 'cors',
            'Access-Control-Allow-Origin': 'http://localhost:80',
            'Access-Control-Max-Age': 3600,
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json',
            },
        })
            .then(result => {
                return result.json();
            })
            .then(res => {
                console.log(res);
                if (res.status === 200 && res.success) {
                    resolve(res.data || { default_admin_id: 0, admins: [] });
                } else {
                    if (res.status === 401) {
                        handleUnauthorized();
                    } else {
                        reject(res.message || 'Ошибка загрузки настроек системного чата');
                    }
                }
            })
            .catch(err => {
                console.log('error: ' + err);
                reject(err);
            });
    });
}

export function saveSystemChatAdminSettings(defaultAdminId) {
    return new Promise(function (resolve, reject) {
        authenticatedFetch(Config.apiDomain + "/v1/settings/system-chat-admin", {
            method: "POST",
            mode: 'cors',
            'Access-Control-Allow-Origin': 'http://localhost:80',
            'Access-Control-Max-Age': 3600,
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                default_admin_id: defaultAdminId
            })
        })
            .then(result => {
                return result.json();
            })
            .then(res => {
                console.log(res);
                if (res.status === 200 && res.success) {
                    resolve(res.data || {});
                } else {
                    if (res.status === 401) {
                        handleUnauthorized();
                    } else {
                        reject(res.message || 'Ошибка сохранения настроек системного чата');
                    }
                }
            })
            .catch(err => {
                console.log('error: ' + err);
                reject(err);
            });
    });
}

export function getHelpbotPolicy() {
    return authenticatedFetch(Config.apiDomain + "/v2/helpbot-settings", {
        method: "GET",
        mode: 'cors',
        headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json',
        },
    }).then(response => {
        if (response.status === 401 && network.history) {
            handleUnauthorized();
        }

        return parseV2Response(response, 'Ошибка загрузки сценария helpbot');
    });
}

export function publishHelpbotPolicy(scenario) {
    return authenticatedFetch(Config.apiDomain + "/v2/helpbot-settings", {
        method: "POST",
        mode: 'cors',
        headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({ scenario })
    }).then(response => {
        if (response.status === 401 && network.history) {
            handleUnauthorized();
        }

        return parseV2Response(response, 'Ошибка публикации сценария');
    });
}

export function validateHelpbotPolicy(scenario) {
    return authenticatedFetch(Config.apiDomain + "/v2/helpbot-settings/validate", {
        method: "POST",
        mode: 'cors',
        headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({ scenario })
    }).then(response => {
        if (response.status === 401 && network.history) {
            handleUnauthorized();
        }

        return parseV2Response(response, 'Ошибка проверки сценария');
    });
}

function getV2ErrorMessage(body, fallbackMessage) {
    if (!body || !body.message) {
        return fallbackMessage;
    }

    return Array.isArray(body.message) ? body.message.join(', ') : String(body.message);
}

function parseV2Response(response, fallbackMessage) {
    return response.json()
        .catch(() => null)
        .then(body => {
            if (!response.ok) {
                const error = new Error(getV2ErrorMessage(body, fallbackMessage));
                error.status = response.status;
                throw error;
            }

            if (!body) {
                throw new Error(fallbackMessage);
            }

            return body;
        });
}

function paymentsAdminRequest(path, options, fallbackMessage, hasBody = true) {
    return authenticatedFetch(Config.apiDomain + path, {
        mode: 'cors',
        headers: {
            'Accept': 'application/json',
            ...(options.body === undefined ? {} : {'Content-Type': 'application/json'}),
        },
        ...options,
        ...(options.body === undefined ? {} : {body: JSON.stringify(options.body)}),
    }).then(response => {
        if (response.status === 401) {
            handleUnauthorized();
        }

        if (hasBody) {
            return parseV2Response(response, fallbackMessage);
        }
        if (response.ok) {
            return undefined;
        }

        return response.json()
            .catch(() => null)
            .then(body => {
                const error = new Error(getV2ErrorMessage(body, fallbackMessage));
                error.status = response.status;
                throw error;
            });
    });
}

export function getPaymentSubscriptionsAdmin() {
    return paymentsAdminRequest(
        '/v2/payments/admin/subscriptions',
        {method: 'GET'},
        'Не удалось загрузить тарифы',
    );
}

export function createPaymentSubscriptionAdmin(subscription) {
    return paymentsAdminRequest(
        '/v2/payments/admin/subscriptions',
        {method: 'POST', body: subscription},
        'Не удалось создать тариф',
    );
}

export function updatePaymentSubscriptionAdmin(subscriptionId, subscription) {
    return paymentsAdminRequest(
        '/v2/payments/admin/subscriptions/' + encodeURIComponent(subscriptionId),
        {method: 'PATCH', body: subscription},
        'Не удалось сохранить тариф',
    );
}

export function disablePaymentSubscriptionAdmin(subscriptionId) {
    return paymentsAdminRequest(
        '/v2/payments/admin/subscriptions/' + encodeURIComponent(subscriptionId),
        {method: 'DELETE'},
        'Не удалось отключить тариф',
        false,
    );
}

export function getPaymentUsersAdmin({
    page = 1,
    limit = 20,
    subscriptionId,
    fio,
    phone,
    email,
} = {}) {
    const query = new URLSearchParams({page: String(page), limit: String(limit)});
    if (subscriptionId) {
        query.set('subscription_id', String(subscriptionId));
    }
    if (fio) {
        query.set('fio', fio);
    }
    if (phone) {
        query.set('phone', phone);
    }
    if (email) {
        query.set('email', email);
    }

    return paymentsAdminRequest(
        '/v2/payments/admin/users?' + query.toString(),
        {method: 'GET'},
        'Не удалось загрузить пользователей',
    );
}

export function getPaymentUserSubscriptionAdmin(userId) {
    return paymentsAdminRequest(
        '/v2/payments/admin/users/' + encodeURIComponent(userId) + '/subscription',
        {method: 'GET'},
        'Не удалось загрузить подписку пользователя',
    );
}

export function assignPaymentSubscriptionAdmin(userId, assignment) {
    return paymentsAdminRequest(
        '/v2/payments/admin/users/' + encodeURIComponent(userId) + '/subscription',
        {method: 'PUT', body: assignment},
        'Не удалось изменить подписку пользователя',
    );
}

export function getPaymentInvoicesAdmin({
    page = 1,
    limit = 20,
    status,
    userId,
    subscriptionId,
} = {}) {
    const query = new URLSearchParams({page: String(page), limit: String(limit)});
    if (status) {
        query.set('status', status);
    }
    if (userId) {
        query.set('user_id', String(userId));
    }
    if (subscriptionId) {
        query.set('subscription_id', String(subscriptionId));
    }

    return paymentsAdminRequest(
        '/v2/payments/admin/invoices?' + query.toString(),
        {method: 'GET'},
        'Не удалось загрузить инвойсы',
    );
}

export function getPaymentInvoiceAdmin(invoiceId) {
    return paymentsAdminRequest(
        '/v2/payments/admin/invoices/' + encodeURIComponent(invoiceId),
        {method: 'GET'},
        'Не удалось загрузить инвойс',
    );
}

export function cancelPaymentInvoiceAdmin(invoiceId) {
    return paymentsAdminRequest(
        '/v2/payments/admin/invoices/' + encodeURIComponent(invoiceId) + '/cancel',
        {method: 'POST'},
        'Не удалось отменить инвойс',
    );
}

export function getPaymentInvoiceEventsAdmin(invoiceId) {
    return paymentsAdminRequest(
        '/v2/payments/admin/invoices/' + encodeURIComponent(invoiceId) + '/events',
        {method: 'GET'},
        'Не удалось загрузить события инвойса',
    );
}

export function getPaymentInvoiceTransactionsAdmin(invoiceId) {
    return paymentsAdminRequest(
        '/v2/payments/admin/invoices/' + encodeURIComponent(invoiceId) + '/transactions',
        {method: 'GET'},
        'Не удалось загрузить транзакции инвойса',
    );
}

export function getInterfaceSettings() {
    return unauthenticatedFetch(Config.apiDomain + '/v2/interface-settings', {
        method: 'GET',
        mode: 'cors',
        headers: {
            'Accept': 'application/json'
        }
    }).then(response => parseV2Response(response, 'Ошибка загрузки настроек интерфейса'));
}

export function updateInterfaceSettings(settings, fallbackMessage = 'Ошибка сохранения настроек интерфейса') {
    return authenticatedFetch(Config.apiDomain + '/v2/interface-settings', {
        method: 'PATCH',
        mode: 'cors',
        headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json',
        },
        body: JSON.stringify(settings)
    }).then(response => {
        if (response.status === 401 && network.history) {
            handleUnauthorized();
        }

        return parseV2Response(response, fallbackMessage);
    });
}

export function updateInterfaceMenuLabels(menuLabels) {
    return updateInterfaceSettings(
        { menuLabels },
        'Ошибка сохранения названий меню'
    );
}

export function updateInterfaceInitialTab(initialTab) {
    return updateInterfaceSettings(
        { initialTab },
        'Ошибка сохранения стартовой вкладки'
    );
}

function handleAuthScreenAdminResponse(response, fallbackMessage, hasBody = true) {
    if (response.status === 401 && network.history) {
        handleUnauthorized();
    }

    if (hasBody) {
        return parseV2Response(response, fallbackMessage);
    }

    if (response.ok) {
        return undefined;
    }

    return response.json()
        .catch(() => null)
        .then(body => {
            const error = new Error(getV2ErrorMessage(body, fallbackMessage));
            error.status = response.status;
            throw error;
        });
}

export function getAuthScreenSettings() {
    return authenticatedFetch(Config.apiDomain + '/v2/auth-screen-settings', {
        method: 'GET',
        mode: 'cors',
        headers: {
            'Accept': 'application/json'
        }
    }).then(response => handleAuthScreenAdminResponse(
        response,
        'Ошибка загрузки документов согласия'
    ));
}

export function createAuthScreenDocument(title, file) {
    const formData = new FormData();
    formData.append('title', title);
    formData.append('file', file);

    return authenticatedFetch(Config.apiDomain + '/v2/auth-screen-settings/documents', {
        method: 'POST',
        mode: 'cors',
        headers: {
            'Accept': 'application/json'
        },
        body: formData
    }).then(response => handleAuthScreenAdminResponse(
        response,
        'Ошибка добавления документа согласия'
    ));
}

export function updateAuthScreenDocument(documentId, title, file) {
    const formData = new FormData();
    formData.append('title', title);
    if (file) {
        formData.append('file', file);
    }

    return authenticatedFetch(
        Config.apiDomain + '/v2/auth-screen-settings/documents/' + documentId,
        {
            method: 'PATCH',
            mode: 'cors',
            headers: {
                'Accept': 'application/json'
            },
            body: formData
        }
    ).then(response => handleAuthScreenAdminResponse(
        response,
        'Ошибка изменения документа согласия'
    ));
}

export function deleteAuthScreenDocument(documentId) {
    return authenticatedFetch(
        Config.apiDomain + '/v2/auth-screen-settings/documents/' + documentId,
        {
            method: 'DELETE',
            mode: 'cors',
            headers: {
                'Accept': 'application/json'
            }
        }
    ).then(response => handleAuthScreenAdminResponse(
        response,
        'Ошибка удаления документа согласия',
        false
    ));
}

function handleEventVideoAdminResponse(response, fallbackMessage, hasBody = true) {
    if (response.status === 401) {
        handleUnauthorized();
    }

    if (hasBody) {
        return parseV2Response(response, fallbackMessage);
    }
    if (response.ok) {
        return undefined;
    }

    return response.json()
        .catch(() => null)
        .then(body => {
            const error = new Error(getV2ErrorMessage(body, fallbackMessage));
            error.status = response.status;
            throw error;
        });
}

export function getEventVideoBlockAdmin() {
    return authenticatedFetch(Config.apiDomain + '/v2/admin/event-video-block', {
        method: 'GET',
        mode: 'cors',
        headers: {'Accept': 'application/json'}
    }).then(response => handleEventVideoAdminResponse(
        response,
        'Ошибка загрузки видеоблока'
    ));
}

export function updateEventVideoBlockAdmin(settings) {
    return authenticatedFetch(Config.apiDomain + '/v2/admin/event-video-block', {
        method: 'PATCH',
        mode: 'cors',
        headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json'
        },
        body: JSON.stringify(settings)
    }).then(response => handleEventVideoAdminResponse(
        response,
        'Ошибка сохранения видеоблока'
    ));
}

export function uploadEventVideoAdmin(title, file) {
    const query = new URLSearchParams({title});
    return authenticatedFetch(
        Config.apiDomain + '/v2/admin/event-video-block/videos?' + query.toString(),
        {
            method: 'POST',
            mode: 'cors',
            headers: {
                'Accept': 'application/json',
                'Content-Type': file.type || 'application/octet-stream'
            },
            body: file
        }
    ).then(response => handleEventVideoAdminResponse(
        response,
        'Ошибка загрузки видео'
    ));
}

export function updateEventVideoAdmin(videoId, title) {
    return authenticatedFetch(
        Config.apiDomain + '/v2/admin/event-video-block/videos/' + encodeURIComponent(videoId),
        {
            method: 'PATCH',
            mode: 'cors',
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({title})
        }
    ).then(response => handleEventVideoAdminResponse(
        response,
        'Ошибка сохранения названия видео'
    ));
}

export function reorderEventVideosAdmin(videoIds) {
    return authenticatedFetch(
        Config.apiDomain + '/v2/admin/event-video-block/videos/order',
        {
            method: 'PUT',
            mode: 'cors',
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({video_ids: videoIds})
        }
    ).then(response => handleEventVideoAdminResponse(
        response,
        'Ошибка изменения порядка видео'
    ));
}

export function deleteEventVideoAdmin(videoId) {
    return authenticatedFetch(
        Config.apiDomain + '/v2/admin/event-video-block/videos/' + encodeURIComponent(videoId),
        {
            method: 'DELETE',
            mode: 'cors',
            headers: {'Accept': 'application/json'}
        }
    ).then(response => handleEventVideoAdminResponse(
        response,
        'Ошибка удаления видео',
        false
    ));
}
